// ============================================================================
// Dictionnaires normalisés — matieres / contenants / transports
//
// Source de vérité : tables DB (matieres, contenants, transports), seedées
// depuis REF_* dans la migration 20260411.
//
// Ce module expose :
//   • Les types TS (Matiere, Contenant, Transport)
//   • Un cache en mémoire chargé une fois par session via loadDictionnaires()
//   • Des helpers de résolution (resolveMatiere, resolveContenant, ...)
//     avec fallback vers les constantes REF_* si le cache n'est pas encore
//     hydraté ou si un code a été supprimé de la DB
//   • Un icon registry centralisé (nom Lucide → composant)
//
// Philosophie : les REF_* constantes restent en place (referentiels.ts) comme
// fallback build-time et source de seed. Le cache DB prend le relais dès que
// loadDictionnaires() a résolu, ce qui permet :
//   1. D'ajouter une matière en DB (via MatiereRequestsPage accept) sans
//      redéploiement
//   2. D'enrichir les attributs d'un contenant/transport côté DB
//   3. De désactiver (is_active = false) sans casser les audits existants
// ============================================================================

import {
  Box, Drum, Container, Package, Layers, ShoppingBasket, Trash2,
  Hand, Forklift, Wrench, Building2,
  Leaf, AlertTriangle, Cog, Scissors, Flame,
  Truck, Bike, ShieldAlert, Droplets,
  Recycle, RefreshCw, ArrowDownCircle, HelpCircle, Skull,
  type LucideIcon,
} from 'lucide-react'
import { supabase } from './supabase'
import { REF_MATIERES, REF_PRE_COLLECTE, REF_CONDITIONNEMENT, REF_EXPEDITION, type RefEntry } from './referentiels'

// ─── Icon registry ──────────────────────────────────────────────────────────
// Map "icon_name" (string stocké en DB) → composant Lucide. Ajouter ici toute
// nouvelle icône utilisée dans les seeds / entrées DB. Si un icon_name est
// inconnu, on renvoie HelpCircle (pas d'exception).

const ICON_REGISTRY: Record<string, LucideIcon> = {
  Box, Drum, Container, Package, Layers, ShoppingBasket, Trash2,
  Hand, Forklift, Wrench, Building2,
  Leaf, AlertTriangle, Cog, Scissors, Flame,
  Truck, Bike, ShieldAlert, Droplets,
  Recycle, RefreshCw, ArrowDownCircle, HelpCircle, Skull,
}

export function resolveIcon(name: string | null | undefined): LucideIcon {
  if (!name) return HelpCircle
  return ICON_REGISTRY[name] ?? HelpCircle
}

/** Liste des icon_name disponibles (pour les selects de l'éditeur matière). */
export const ICON_NAMES = Object.keys(ICON_REGISTRY).sort()

// ─── Types ──────────────────────────────────────────────────────────────────

export type Etat = 'solide' | 'liquide' | 'pateux' | 'pulverulent'
export type Macro = 'DIB' | 'AMV' | 'DID'

export interface Matiere {
  id: string
  code: string
  label: string
  icon_name: string
  color: string | null
  macro: Macro | null
  etat: Etat | null
  formes: string[]
  is_dangereux: boolean
  densite_kg_m3: number | null
  is_active: boolean
  /** Code CED par défaut (nomenclature déchets). Pré-remplit le flux à la création ; FK ref_waste_codes. */
  code_dechet: string | null
  /** Chemin Supabase Storage bucket dictionnaire-photos. Convention : matieres/{id}.{ext}. */
  photo_path: string | null
  /** Horodatage DB (select('*')). Utilisé comme jeton d'optimistic lock côté édition. */
  updated_at?: string | null
}

export interface Contenant {
  id: string
  code: string
  label: string
  icon_name: string
  etats_compat: string[]
  is_active: boolean
  /** Type physique (benne/bac/big_bag/caisse/fut/grv/palette/...) */
  type: string | null
  /** Volume normalise en m³. NULL si pas applicable (vrac, palette, balle). */
  volume_m3: number | null
  /** Ouverture (benne) : ouverte/fermee/basculante/papillon/pelican. NULL sinon. */
  ouverture: string | null
  /** Domaine d'usage : interne (tri sur site) / externe (collecte/transport) / mixte. */
  usage: string
  /** Texte libre, format ex. "1.2×0.8×0.6 m". Display only. */
  dimensions: string | null
  /** Contenant adapte aux matieres dangereuses (UN-approved, etc.). */
  is_adr: boolean
  /** Chemin Supabase Storage bucket dictionnaire-photos. Convention : contenants/{id}.{ext}. */
  photo_path: string | null
  /** Horodatage DB (select('*')). Utilisé comme jeton d'optimistic lock côté édition. */
  updated_at?: string | null
}

export interface Transport {
  id: string
  code: string
  label: string
  icon_name: string
  is_adr: boolean
  est_citerne: boolean
  /** Charge utile en tonnes. Decimales autorisees (ex. VL = 1.5 t). */
  charge_utile_t: number | null
  is_active: boolean
  /** Type physique (porteur/ampliroll/bom/citerne/vl/fourgon/grue/autre). */
  type: string | null
  /** Chemin Supabase Storage bucket dictionnaire-photos. Convention : transports/{id}.{ext}. */
  photo_path: string | null
  /** Hayon elevateur (pour palettes / charges lourdes sans quai). */
  has_hayon: boolean
  /** Grappin pour vrac (bois, metal, dechets verts). */
  has_grappin: boolean
  /** Horodatage DB (select('*')). Utilisé comme jeton d'optimistic lock côté édition. */
  updated_at?: string | null
}

/**
 * Prestation : 4e dictionnaire (cree le 04/05/2026, migration prestations_dico_create).
 * Pivot du catalogue : has_matiere/has_contenant/has_transport indiquent quelles
 * dimensions sont REQUISES ou INTERDITES sur une ligne catalogue selon le type.
 * Validation cote DB par le trigger fn_check_catalogue_consistency.
 */
export interface Prestation {
  id: string
  code: string
  label: string
  icon_name: string
  has_matiere: boolean
  has_contenant: boolean
  has_transport: boolean
  default_unit: string | null
  is_active: boolean
  /** Horodatage DB (select('*')). Utilisé comme jeton d'optimistic lock côté édition. */
  updated_at?: string | null
}

// ─── Cache en mémoire ───────────────────────────────────────────────────────

interface DictCache {
  matieres: Matiere[]
  contenants: Contenant[]
  transports: Transport[]
  prestations: Prestation[]
  loaded: boolean
  loadedAt: number | null
}

const cache: DictCache = {
  matieres: [],
  contenants: [],
  transports: [],
  prestations: [],
  loaded: false,
  loadedAt: null,
}

let loadingPromise: Promise<void> | null = null

/**
 * Charge les 3 dictionnaires depuis la DB. Idempotent : appels multiples
 * renvoient la même promesse tant que le chargement n'est pas terminé.
 * Appeler au bootstrap de l'app (main.tsx) pour hydrater avant tout render.
 */
export function loadDictionnaires(): Promise<void> {
  if (cache.loaded) return Promise.resolve()
  if (loadingPromise) return loadingPromise

  loadingPromise = (async () => {
    const [matRes, cntRes, trpRes, presRes] = await Promise.all([
      supabase.from('matieres').select('*').eq('is_active', true).order('label'),
      supabase.from('contenants').select('*').eq('is_active', true).order('label'),
      supabase.from('transports').select('*').eq('is_active', true).order('label'),
      supabase.from('prestations').select('*').eq('is_active', true).order('label'),
    ])

    if (!matRes.error && matRes.data) cache.matieres = matRes.data as Matiere[]
    if (!cntRes.error && cntRes.data) cache.contenants = cntRes.data as Contenant[]
    if (!trpRes.error && trpRes.data) cache.transports = trpRes.data as Transport[]
    if (!presRes.error && presRes.data) cache.prestations = presRes.data as Prestation[]

    cache.loaded = true
    cache.loadedAt = Date.now()
    loadingPromise = null
  })()

  return loadingPromise
}

/** Force un refresh du cache (ex: après un INSERT depuis MatiereRequestsPage). */
export function invalidateDictionnaires(): void {
  cache.loaded = false
  cache.loadedAt = null
  loadingPromise = null
}

/** Accès direct au cache (lecture seule). Utilisé par les pickers. */
export function getDictionnaires() {
  return {
    matieres: cache.matieres,
    contenants: cache.contenants,
    transports: cache.transports,
    prestations: cache.prestations,
    loaded: cache.loaded,
  }
}

// ─── Résolveurs avec fallback REF_* ─────────────────────────────────────────

/**
 * Résout un code matière en entrée unifiée (label + icon + color).
 * Lit le cache DB en priorité, tombe en fallback sur REF_MATIERES si rien.
 */
export function resolveMatiere(code: string | null | undefined): RefEntry | null {
  if (!code) return null

  // 1. Cache DB
  const hit = cache.matieres.find(m => m.code === code)
  if (hit) {
    return {
      code: hit.code,
      label: hit.label,
      icon: resolveIcon(hit.icon_name),
      color: hit.color ?? undefined,
    }
  }

  // 2. Fallback constante
  return REF_MATIERES.find(e => e.code === code) ?? null
}

export function resolveContenant(code: string | null | undefined): RefEntry | null {
  if (!code) return null

  const hit = cache.contenants.find(c => c.code === code)
  if (hit) {
    return {
      code: hit.code,
      label: hit.label,
      icon: resolveIcon(hit.icon_name),
    }
  }

  // Fallback : on cherche d'abord dans REF_CONDITIONNEMENT puis REF_PRE_COLLECTE
  return REF_CONDITIONNEMENT.find(e => e.code === code)
    ?? REF_PRE_COLLECTE.find(e => e.code === code)
    ?? null
}

export function resolveTransport(code: string | null | undefined): RefEntry | null {
  if (!code) return null

  const hit = cache.transports.find(t => t.code === code)
  if (hit) {
    return {
      code: hit.code,
      label: hit.label,
      icon: resolveIcon(hit.icon_name),
    }
  }

  return REF_EXPEDITION.find(e => e.code === code) ?? null
}

// ─── Matière complète (incluant attributs DB) ───────────────────────────────
// Quand on a besoin des attributs (etat, formes, is_dangereux) — ex. dans la
// CataloguePage pour valider la compatibilité matière × contenant.

export function getMatiereFull(code: string | null | undefined): Matiere | null {
  if (!code) return null
  return cache.matieres.find(m => m.code === code) ?? null
}

export function getContenantFull(code: string | null | undefined): Contenant | null {
  if (!code) return null
  return cache.contenants.find(c => c.code === code) ?? null
}

export function getTransportFull(code: string | null | undefined): Transport | null {
  if (!code) return null
  return cache.transports.find(t => t.code === code) ?? null
}

/**
 * Resout un code prestation. Pas de fallback REF_* (dico cree apres,
 * sans constantes legacy).
 */
export function resolvePrestation(code: string | null | undefined): Prestation | null {
  if (!code) return null
  return cache.prestations.find(p => p.code === code) ?? null
}

export function getPrestationFull(code: string | null | undefined): Prestation | null {
  return resolvePrestation(code)
}

// ─── Listes actives (pour les selects / pickers) ───────────────────────────

export function getAllMatieres(): Matiere[] {
  if (!cache.loaded) {
    // Fallback : synthétise depuis REF_MATIERES quand le cache n'est pas prêt
    return REF_MATIERES.map(e => ({
      id: '',
      code: e.code,
      label: e.label,
      icon_name: '',
      color: e.color ?? null,
      macro: null,
      etat: null,
      formes: [],
      is_dangereux: false,
      densite_kg_m3: null,
      is_active: true,
      code_dechet: null,
      photo_path: null,
    }))
  }
  return cache.matieres
}

export function getAllContenants(): Contenant[] {
  if (!cache.loaded) {
    return REF_CONDITIONNEMENT.map(e => ({
      id: '',
      code: e.code,
      label: e.label,
      icon_name: '',
      etats_compat: [],
      is_active: true,
      type: null,
      volume_m3: null,
      ouverture: null,
      usage: 'externe',
      dimensions: null,
      is_adr: false,
      photo_path: null,
    }))
  }
  return cache.contenants
}

export function getAllTransports(): Transport[] {
  if (!cache.loaded) {
    return REF_EXPEDITION.map(e => ({
      id: '',
      code: e.code,
      label: e.label,
      icon_name: '',
      is_adr: false,
      est_citerne: false,
      charge_utile_t: null,
      is_active: true,
      type: null,
      photo_path: null,
      has_hayon: false,
      has_grappin: false,
    }))
  }
  return cache.transports
}

/**
 * Liste des prestations actives. Pas de fallback REF_* (nouveau dico),
 * renvoie [] si le cache n'est pas encore hydrate. Les composants doivent
 * attendre loadDictionnaires() avant de l'utiliser pour des selects.
 */
export function getAllPrestations(): Prestation[] {
  return cache.prestations
}

// ─── Adaptateurs RefEntry (pour les pickers existants) ─────────────────────
// Les pickers de l'app (MatierePicker notamment) consomment RefEntry. Ces
// helpers reconstruisent des RefEntry[] depuis le cache DB (ou le fallback)
// pour une substitution plug-and-play des REF_* constants.

export function getAllMatieresAsRefEntries(): RefEntry[] {
  if (!cache.loaded) return REF_MATIERES
  return cache.matieres.map(m => ({
    code: m.code,
    label: m.label,
    icon: resolveIcon(m.icon_name),
    color: m.color ?? undefined,
    macro: m.macro ?? null,
  }))
}

export function getAllContenantsAsRefEntries(): RefEntry[] {
  if (!cache.loaded) return REF_CONDITIONNEMENT
  return cache.contenants.map(c => ({
    code: c.code,
    label: c.label,
    icon: resolveIcon(c.icon_name),
  }))
}

export function getAllTransportsAsRefEntries(): RefEntry[] {
  if (!cache.loaded) return REF_EXPEDITION
  return cache.transports.map(t => ({
    code: t.code,
    label: t.label,
    icon: resolveIcon(t.icon_name),
  }))
}
